import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Drawer, Heading } from "@foris/avocado-suite";
import { useLayoutStore } from "@context/useLayoutStore";
import ListLabels from "@dimensions/group/EditLabels/ListLabels";
import SelectLabels from "@dimensions/group/EditLabels/SelectLabels";
import styles from "./tagsDrawer.module.scss";
import { ContextEditLabel } from "@dimensions/group/EditLabels/ContextEditLabels";
import { Label } from "@models/ISchema";

interface TagsDrawerProps {
  isOpen?: boolean;
  labels?: Label[];
  canEdit?: boolean;
  isLoadingLabels?: boolean;
  isLabelDisabled?: (label: Label) => boolean;
  onSaveLabels?: () => void;
  onClose?: () => void;
}

const getLabelKey = (label: Label) => {
  return `${label?.id}-${label?.code}-${label?.category?.id}`;
};

const TagsDrawer = ({
  isOpen,
  labels = [],
  canEdit,
  isLoadingLabels,
  isLabelDisabled,
  onSaveLabels,
  onClose,
}: TagsDrawerProps) => {
  const { t } = useTranslation();
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const contextEdit = useContext(ContextEditLabel);
  const [ogLabels, setOgLabels] = useState<Label[]>([]);

  useEffect(() => {
    if (isOpen && !isLoadingLabels) {
      const adaptedLabels = (labels ?? [])?.map(label => ({ ...label, isNew: false }));

      setOgLabels(structuredClone(adaptedLabels));
      contextEdit.setLabels(structuredClone(adaptedLabels));
    }
  }, [isOpen, isLoadingLabels]);

  const ogLabelsByIdAndCode = useMemo(() => {
    return ogLabels?.reduce((acc, label) => {
      acc[getLabelKey(label)] = label;
      return acc;
    }, {});
  }, [ogLabels]);

  const hasChanges = useMemo(() => {
    if (ogLabels?.length !== contextEdit?.labels?.length) {
      return true;
    }

    return (contextEdit?.labels ?? [])?.some(label => {
      const ogLabel = ogLabelsByIdAndCode[getLabelKey(label)];
      return label?.isNew || !ogLabel;
    });
  }, [ogLabels, contextEdit.labels]);

  return (
    <Drawer
      className={styles.tagsDrawer}
      isOpen={isOpen}
      heightGap={visibleHeaderPixels}
      title={
        <Heading type="h3">
          {canEdit ? t("edit-tag-manually.title") : t("edit-tag-manually.title-short")}
        </Heading>
      }
      onClose={onClose}
      closeOnOverlayClick
      footer={
        <footer className={styles.tagsDrawer_actionsButton}>
          <Button variant="ghost" onClick={onClose}>
            {t("generals.cancel")}
          </Button>

          {canEdit && (
            <Button variant="primary" disabled={!hasChanges} onClick={onSaveLabels}>
              {t("generals.save-changes")}
            </Button>
          )}
        </footer>
      }
    >
      {canEdit && (
        <SelectLabels
          className={styles.tagsDrawer_customSelect}
          label={t("edit-tag-manually.select-option.label")}
          isLabelDisabled={isLabelDisabled}
        />
      )}

      <ListLabels canEdit={canEdit} />
    </Drawer>
  );
};

export default TagsDrawer;
